/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Country and subdivision option lists for address entry, ISO 3166-1 alpha-2 and ISO 3166-2.
 *
 * The dataset is ~250 countries and ~4,400 subdivisions, so it is pulled in with a dynamic import
 * and lands in its own chunk rather than the main bundle. Country names come from
 * `Intl.DisplayNames`, which is localised for every UI language; subdivision names ship English
 * only, which is tolerable because ISO spells most of them in the local language already
 * ("Baja California", "Bío-Bío").
 *
 * Stored values are always codes ("ON", "SP", "QLD"). Free text collected before this existed is
 * folded onto those codes by {@link normaliseCountry} and {@link normaliseSubdivision} so a profile
 * saved years ago still selects the right entry instead of showing up blank.
 */

/** One selectable entry; `code` is what gets persisted, `name` is what the user reads. */
export interface GeoOption {
  code: string;
  name: string;
}

type Subdivision = [name: string, code: string];
type CountryRow = [name: string, code: string, subdivisions: Subdivision[]];

let dataset: Promise<CountryRow[]> | null = null;

/** Loads (once) and caches the ISO dataset chunk. */
function load(): Promise<CountryRow[]> {
  dataset ??= import('country-region-data').then(
    (mod) => mod.allCountries as unknown as CountryRow[],
  );
  return dataset;
}

/**
 * Country names in the user's language, sorted the way that language sorts. Falls back to the
 * dataset's own English name if the runtime cannot name a region.
 */
export async function loadCountries(locale: string): Promise<GeoOption[]> {
  const rows = await load();
  const display = regionNamer(locale);
  const collator = new Intl.Collator(locale);
  return rows
    .map(([name, code]) => ({ code, name: display(code) || name }))
    .sort((a, b) => collator.compare(a.name, b.name));
}

/** Subdivisions for one country, in ISO order. Empty when the country has none on record. */
export async function loadSubdivisions(country: string): Promise<GeoOption[]> {
  const row = await find(country);
  return row ? row[2].map((sub) => ({ code: identify(sub), name: sub[0] })) : [];
}

/**
 * What gets persisted for a subdivision. Normally the ISO code, but a few entries carry none
 * (Macedonia's Radoviš), and there the name is the only stable identifier left.
 */
function identify(sub: Subdivision): string {
  return sub[1] || sub[0];
}

/**
 * Folds a stored country onto an ISO alpha-2 code, or null when it is not a country at all.
 *
 * Deliberately refuses to guess: production holds twelve rows reading "GM North America", which
 * names a sales region spanning three countries. Returning null puts an empty picker in front of
 * the user rather than inventing a country on their behalf.
 */
export async function normaliseCountry(raw: string | null | undefined): Promise<string | null> {
  const value = squash(raw);
  if (!value) return null;
  const rows = await load();
  const byCode = rows.find(([, code]) => code === value);
  if (byCode) return byCode[1];
  const byName = rows.find(([name]) => squash(name) === value);
  return byName ? byName[1] : null;
}

/**
 * Every country whose ISO 3166-2 list holds this subdivision, matched on either code or name.
 *
 * GDL routinely answers with a state and no country: "CALIFORNIA" and nothing else. The country is
 * not really unknown there, it just went unsaid, and only the United States has a California. One
 * hit therefore settles the country; several narrow it to a shortlist ("Florida" is also in Puerto
 * Rico and Uruguay); none leaves the question open.
 *
 * Matching is exact, unlike {@link normaliseSubdivision}, which may fall back to a prefix. A near
 * miss here would file the dealership under the wrong country, which is worse than asking.
 */
export async function countriesForSubdivision(raw: string | null | undefined): Promise<string[]> {
  const value = squash(raw);
  if (!value) return [];
  const rows = await load();
  return rows
    .filter(([, , subdivisions]) =>
      subdivisions.some(([name, code]) => squash(name) === value || squash(code) === value),
    )
    .map(([, code]) => code);
}

/** Folds a stored state or province onto its ISO code within `country`, or null when unknown. */
export async function normaliseSubdivision(
  country: string | null | undefined,
  raw: string | null | undefined,
): Promise<string | null> {
  const value = squash(raw);
  const iso = squash(country);
  if (!value || !iso) return null;
  const row = await find(iso);
  if (!row) return null;
  const byCode = row[2].find(([, code]) => code && code.toUpperCase() === value);
  if (byCode) return byCode[1];
  const byName = row[2].find(([name]) => squash(name) === value);
  if (byName) return identify(byName);
  const alias = ALIASES[`${iso}:${value}`];
  if (alias) return alias;
  return byUniquePrefix(row[2], value);
}

/**
 * Last resort for stored names that are shorter than the ISO spelling: "COAHUILA" for "Coahuila de
 * Zaragoza", "VALLE DEL" for "Valle del Cauca". Only accepted when exactly one subdivision starts
 * that way, so "BAJA CALIFORNIA" never silently becomes "Baja California Sur".
 */
function byUniquePrefix(subdivisions: Subdivision[], value: string): string | null {
  const matches = subdivisions.filter(([name]) => squash(name).startsWith(`${value} `));
  return matches.length === 1 ? identify(matches[0]) : null;
}

/**
 * Spellings the ISO table will not match on its own, taken from what production actually holds:
 * Canada shortens three of its territories, Chile still uses the roman numerals that ISO codes
 * replaced, and one Mexican state is simply misspelt.
 *
 * "MEXICO" is deliberately absent. It reads as either Ciudad de México or Estado de México, both
 * real, so the picker is left empty for that user to answer rather than guessed at.
 */
const ALIASES: Record<string, string> = {
  'CA:NEWFOUNDLAND': 'NL',
  'CA:YUKON TERRITORY': 'YT',
  'CA:NORTHWEST TERRITORY': 'NT',
  'MX:AQUASCALIENTES': 'AGU',
  'CL:I': 'TA',
  'CL:II': 'AN',
  'CL:III': 'AT',
  'CL:IV': 'CO',
  'CL:V': 'VS',
  'CL:VI': 'LI',
  'CL:VII': 'ML',
  'CL:VIII': 'BI',
  'CL:IX': 'AR',
  'CL:X': 'LL',
  'CL:XI': 'AI',
  'CL:XII': 'MA',
  'CL:XIV': 'LR',
  'CL:XV': 'AP',
};

async function find(country: string): Promise<CountryRow | undefined> {
  const iso = squash(country);
  return (await load()).find(([, code]) => code === iso);
}

/**
 * Upper-cased, whitespace-collapsed and stripped of accents, so "  british  columbia " matches
 * "British Columbia" and the unaccented "QUERETARO" that production stores matches ISO's
 * "Querétaro". Folding runs both ways, which also rescues rows typed with accents the ISO table
 * omits ("ASUNCIÓN" against "Asuncion").
 */
function squash(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');
}

/** `Intl.DisplayNames` is unavailable on a few older runtimes; English names stand in there. */
function regionNamer(locale: string): (code: string) => string | undefined {
  try {
    const names = new Intl.DisplayNames([locale], { type: 'region' });
    return (code) => names.of(code);
  } catch {
    return () => undefined;
  }
}
